import { Disposable } from '@gitlab-org/disposable';
import { Transport } from '@gitlab-org/webview-transport';
import { WebviewPlugin } from '@gitlab-org/webview-plugin';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { ThemeProvider } from '@gitlab-org/webview-theme';
import { ExtensionMessageBusProvider } from '../types';
import { WebviewRuntimeMessageBus } from '../events';
import { setupTransports } from './transport';
import { setupWebviewPlugins } from './plugin';

export type SetupWebviewRuntimeProps = {
  extensionMessageBusProvider: ExtensionMessageBusProvider;
  transports: Transport[];
  plugins: WebviewPlugin[];
  logger: Logger;
  themeProvider: ThemeProvider;
};

export function setupWebviewRuntime(props: SetupWebviewRuntimeProps): Disposable {
  const logger = withPrefix(props.logger, '[Webview]');
  logger.debug('Setting up webview runtime');

  try {
    const runtimeMessageBus = new WebviewRuntimeMessageBus();
    const transportDisposable = setupTransports(props.transports, runtimeMessageBus, logger);
    const webviewPluginDisposable = setupWebviewPlugins({
      runtimeMessageBus,
      logger,
      plugins: props.plugins,
      extensionMessageBusProvider: props.extensionMessageBusProvider,
      themeProvider: props.themeProvider,
    });

    logger.info('Webview runtime setup completed successfully');

    return {
      dispose: () => {
        logger.debug('Disposing webview runtime');
        transportDisposable.dispose();
        webviewPluginDisposable.dispose();
        logger.info('Webview runtime disposed');
      },
    };
  } catch (error) {
    props.logger.error(
      'Failed to setup webview runtime',
      error instanceof Error ? error : undefined,
    );

    return {
      dispose: () => {
        logger.debug('Disposing empty webview runtime due to setup failure');
      },
    };
  }
}
